import { useGetList, useListContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { formatMxn } from "../quoter/PlanSchedule";
import { CancelledChip, InvoicedPill, usePaidByLabel } from "./ExpenseBadges";
import { ExpenseMonthlyTotals } from "./ExpenseMonthlyTotals";
import { ExpenseReimbursements } from "./ExpenseReimbursements";
import { expensesExporter } from "./expensesExporter";
import {
  formatExpenseDate,
  monthToRange,
  rangeToMonth,
  toIdentifier,
} from "./expenseScreenRules";
import {
  EXPENSE_PAID_BY_VALUES,
  type Expense,
  type ExpenseCategory,
} from "./expenseTypes";
import { useExpenseTotal } from "./useExpenseTotal";

const CONTROL_CLASS =
  "h-11 rounded-md border bg-background px-3 text-base disabled:opacity-60";

/** Header total of the CURRENT filtered view (cancelled rows excluded); never per person. */
const ExpenseTotal = () => {
  const translate = useTranslate();
  const { filterValues } = useListContext();
  const { data, isPending, isError } = useExpenseTotal(filterValues);
  return (
    <div
      data-testid="expenses-total"
      className="flex flex-wrap items-baseline gap-2 rounded-lg bg-card p-4 shadow-sm"
    >
      <span className="text-sm text-muted-foreground">
        {translate("crm.expenses.total_label")}
      </span>
      <span className="text-2xl font-bold tabular-nums text-primary">
        {isPending || isError || data == null ? "—" : formatMxn(data)}
      </span>
    </div>
  );
};

const ExpenseFilters = () => {
  const translate = useTranslate();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const { data: categories } = useGetList<ExpenseCategory>(
    "expense_categories",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "name", order: "ASC" },
    },
  );

  const update = (patch: Record<string, unknown>) => {
    const next = Object.fromEntries(
      Object.entries({ ...filterValues, ...patch }).filter(
        ([, value]) => value !== undefined && value !== "",
      ),
    );
    // Immediate (not debounced): the list must narrow as soon as a filter changes.
    setFilters(next, displayedFilters, false);
  };

  const invoiced =
    filterValues.is_invoiced === undefined
      ? ""
      : String(filterValues.is_invoiced);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="expense-filter-month">
          {translate("crm.expenses.filters.month")}
        </Label>
        <input
          id="expense-filter-month"
          type="month"
          className={CONTROL_CLASS}
          value={rangeToMonth(filterValues["expense_date@gte"])}
          onChange={(event) => {
            const range = monthToRange(event.target.value);
            update({
              "expense_date@gte": range?.from,
              "expense_date@lte": range?.to,
            });
          }}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="expense-filter-category">
          {translate("crm.expenses.filters.category")}
        </Label>
        <select
          id="expense-filter-category"
          className={CONTROL_CLASS}
          value={String(filterValues.category_id ?? "")}
          onChange={(event) =>
            update({
              category_id: event.target.value
                ? toIdentifier(event.target.value)
                : undefined,
            })
          }
        >
          <option value="">{translate("crm.expenses.filters.all")}</option>
          {(categories ?? []).map((category) => (
            <option key={category.id} value={String(category.id)}>
              {category.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="expense-filter-paid-by">
          {translate("crm.expenses.filters.paid_by")}
        </Label>
        <select
          id="expense-filter-paid-by"
          className={CONTROL_CLASS}
          value={String(filterValues.paid_by ?? "")}
          onChange={(event) =>
            update({ paid_by: event.target.value || undefined })
          }
        >
          <option value="">{translate("crm.expenses.filters.all")}</option>
          {EXPENSE_PAID_BY_VALUES.map((value) => (
            <option key={value} value={value}>
              {translate(`crm.expenses.paid_by_values.${value}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="expense-filter-invoiced">
          {translate("crm.expenses.filters.invoiced")}
        </Label>
        <select
          id="expense-filter-invoiced"
          className={CONTROL_CLASS}
          value={invoiced}
          onChange={(event) =>
            update({
              is_invoiced:
                event.target.value === ""
                  ? undefined
                  : event.target.value === "true",
            })
          }
        >
          <option value="">{translate("crm.expenses.filters.all")}</option>
          <option value="true">{translate("crm.expenses.filters.yes")}</option>
          <option value="false">{translate("crm.expenses.filters.no")}</option>
        </select>
      </div>
    </div>
  );
};

const ExpenseListBody = () => {
  const translate = useTranslate();
  const paidByLabel = usePaidByLabel();
  return (
    <div className="space-y-4">
      <ExpenseTotal />
      <ExpenseFilters />
      <DataTable
        rowClick="show"
        bulkActionButtons={false}
        rowClassName={(record: Expense) =>
          record.cancelled_at ? "opacity-60" : undefined
        }
      >
        <DataTable.Col
          source="expense_date"
          label="crm.expenses.fields.date"
          className="tabular-nums"
          render={(record: Expense) => formatExpenseDate(record.expense_date)}
        />
        <DataTable.Col source="concept" label="crm.expenses.fields.concept" />
        <DataTable.Col
          source="category_name"
          label="crm.expenses.fields.category"
        />
        <DataTable.Col
          source="supplier"
          label="crm.expenses.fields.supplier"
          render={(record: Expense) => record.supplier ?? "—"}
        />
        <DataTable.Col
          source="amount_mxn"
          label="crm.expenses.fields.amount"
          className="text-right tabular-nums"
          render={(record: Expense) => formatMxn(record.amount_mxn)}
        />
        <DataTable.Col
          source="iva_amount"
          label="crm.expenses.fields.iva"
          className="text-right tabular-nums"
          render={(record: Expense) =>
            record.has_iva ? formatMxn(record.iva_amount) : "—"
          }
        />
        <DataTable.Col
          source="is_invoiced"
          label="crm.expenses.fields.invoiced"
          render={(record: Expense) => (
            <InvoicedPill invoiced={record.is_invoiced} />
          )}
        />
        <DataTable.Col
          source="paid_by"
          label="crm.expenses.fields.paid_by"
          render={(record: Expense) => paidByLabel(record)}
        />
        <DataTable.Col
          source="cancelled_at"
          label="crm.expenses.fields.status"
          render={(record: Expense) =>
            record.cancelled_at ? (
              <CancelledChip />
            ) : (
              translate("crm.expenses.active")
            )
          }
        />
      </DataTable>
    </div>
  );
};

const ExpenseListActions = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button asChild variant="outline" className="min-h-11">
        <Link to="/expense_categories">
          {translate("crm.expenses.categories.link")}
        </Link>
      </Button>
      <Button asChild variant="outline" className="min-h-11">
        <Link to={ExpenseMonthlyTotals.path}>
          {translate("crm.expenses.totals.link")}
        </Link>
      </Button>
      <Button asChild variant="outline" className="min-h-11">
        <Link to={ExpenseReimbursements.path}>
          {translate("crm.expenses.reimbursement.link")}
        </Link>
      </Button>
      {/* Hidden by `ExportButton` itself for any role without export access. */}
      <ExportButton className="min-h-11 cursor-pointer" maxResults={10000} />
      <CreateButton label="crm.expenses.new" />
    </div>
  );
};

/**
 * Almikia Epic EG (TASK-044): "Gastos" over `expenses_summary` for direccion,
 * lead_manager, administracion and contabilidad (the route is gated by
 * `canAccess`). Newest first, server-side pagination, search by concept or
 * supplier, filters by month / category / payer / invoiced, and a header total
 * of the filtered view that excludes cancelled rows. No per-person comparison.
 */
export const ExpenseList = () => {
  const translate = useTranslate();
  return (
    <List
      resource="expenses"
      sort={{ field: "expense_date", order: "DESC" }}
      perPage={25}
      exporter={expensesExporter}
      filters={[
        <SearchInput
          key="q"
          source="q"
          alwaysOn
          className="min-w-64"
          placeholder={translate("crm.expenses.search")}
        />,
      ]}
      actions={<ExpenseListActions />}
    >
      <ExpenseListBody />
    </List>
  );
};
